import { useEffect, useRef, type RefObject } from 'react';
import { escapeSpent, nextOverlayOrder, registerDialogOrder } from '@/components/overlayEscape';

/*
 * The page's own tab order, which this trap replays inside the dialog. Two things it got wrong
 * (AC-I0557-3): a control at `tabindex="-1"` is not a stop — a roving toolbar puts every button but
 * one there, so the trap walked the markdown toolbar button by button — and a `contenteditable`
 * surface is one, so the editor itself was never reached by Tab inside a dialog or a sheet.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(', ');

/** Fields iOS opens a picker for on focus: not focused when a dialog opens (I-1342). */
const OPENS_A_PICKER = ['date', 'datetime-local', 'time', 'month', 'week']
  .map((type) => `input[type="${type}"]`)
  .join(', ');

/** A field a person types into: what a form dialog opens on (KD-16). */
const TAKES_TYPING = [
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  'input:not([type])',
  ...['text', 'search', 'email', 'url', 'tel', 'number', 'password'].map(
    (type) => `input[type="${type}"]`,
  ),
].join(', ');

/** The surface's corner controls (`PanelControls`): its way out, never where it opens (KD-16). */
const CORNER_CONTROLS = '[data-testid="panel-controls"]';

/**
 * Where focus goes when a dialog opens and nothing inside took it (KD-16, I-1551): its first field
 * a person types into, else its first control that is not the corner's, else the dialog itself.
 * KD-11 put every dialog's close at its header's right end, first in the source, so "the first
 * control" was the close: *Postpone* opened on Close and the first key typed went nowhere. Never a
 * field iOS answers with its own picker either (I-1342): the date picker rose over the Postpone
 * dialog's words as it opened, so a dialog whose content starts with one takes focus itself.
 */
export function initialFocusTarget(container: HTMLElement): HTMLElement {
  return initialFocusCandidates(container)[0];
}

/** `initialFocusTarget`'s choices in its order, the dialog itself last: the first that takes focus. */
function initialFocusCandidates(container: HTMLElement): HTMLElement[] {
  const controls = contentControls(container);
  const picker = controls.findIndex((element) => element.matches(OPENS_A_PICKER));
  return [
    ...typingFields(container),
    ...controls.slice(0, picker === -1 ? controls.length : picker),
    container,
  ];
}

function contentControls(container: HTMLElement): HTMLElement[] {
  return focusableElements(container).filter((element) => !element.closest(CORNER_CONTROLS));
}

function typingFields(container: HTMLElement): HTMLElement[] {
  return contentControls(container).filter((element) => element.matches(TAKES_TYPING));
}

/**
 * Focuses the first of `candidates` that takes focus, and returns it. A control can be in the dialog
 * and still not take it (KD-16a, I-1551): Crepe keeps its link tooltip — a link, an input and five
 * buttons — inside the editor, undrawn until it is needed, and `focus()` on any of them does nothing.
 * The trap counted them as stops, so Tab from the reason editor and Shift-Tab from Cancel stayed put.
 * Whether focus moved is read after the call, not from layout, which jsdom has none of.
 */
function focusFirstThatTakes(
  candidates: HTMLElement[],
  options?: FocusOptions,
): HTMLElement | null {
  for (const candidate of candidates) {
    candidate.focus(options);
    if (document.activeElement === candidate) return candidate;
  }
  return null;
}

/**
 * How long after it opens a dialog may still hand focus to a field that mounts late (KD-16a). The
 * markdown editor draws its editable surface a few hundred ms after the dialog (measured: absent at
 * 100 ms, present from 400 ms), so the dialog's first field is not there when the trap looks.
 */
const LATE_FIELD_MS = 5_000;

/**
 * An inline edit field in a dialog marks itself with this attribute, and its Escape cancels the edit
 * and nothing else (I-1394): a second Escape then closes the dialog. The trap listens on the dialog's
 * own element, which a keydown reaches before React's handlers at the app's root, so the field's
 * `stopPropagation` comes too late to keep the trap from closing the dialog under it.
 */
export const INLINE_EDIT_ATTRIBUTE = 'data-inline-edit';

export function focusableElements(container: HTMLElement): HTMLElement[] {
  // Not filtered on layout (`offsetParent`/`getClientRects`): jsdom performs no layout at all, so
  // every element measures as zero-size there, and a check that relies on it would pass only
  // against a real browser — exactly the harness-emulates-an-environment trap. `hidden` and
  // `display: none` are readable without layout and are the only way a dialog's own contents are
  // expected to hide a focusable element from itself.
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      !element.hidden &&
      element.style.display !== 'none' &&
      element.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * The control a press last landed on, for a dialog it opens. WebKit does not focus a button on a
 * click or a tap (Safari on the Mac, and every iPhone), so `document.activeElement` is still the
 * page's `body` when the dialog mounts and focus had nowhere to go back to on close (IU-3b,
 * measured in WebKit on the sheet). Read in the capture phase, before the click's own handler opens
 * anything; kept only briefly, so a later dialog opened by a timer does not return to it.
 */
let lastPressed: { element: HTMLElement; at: number } | null = null;
const PRESS_MEMORY_MS = 1_000;
const PRESSABLE = 'button, a[href], [role="button"], [role="menuitem"], [tabindex]';
if (typeof document !== 'undefined') {
  document.addEventListener(
    'click',
    (event) => {
      const element = (event.target as Element | null)?.closest?.(PRESSABLE);
      if (element instanceof HTMLElement) lastPressed = { element, at: performance.now() };
    },
    { capture: true },
  );
}

/**
 * The pointer of the last press, for `holdKeyboardForDialog`: a touch or a pen raises a software
 * keyboard for a field it focuses, a mouse does not. Read in the capture phase, like `lastPressed`.
 */
let lastPointer: { type: string; at: number } | null = null;
if (typeof document !== 'undefined') {
  // The press's end as well as its start: a drag that ends in a dialog (a card dropped on Done) can
  // last longer than the press memory, and its end is the gesture the dialog opens in.
  for (const type of ['pointerdown', 'pointerup']) {
    document.addEventListener(
      type,
      (event) => {
        lastPointer = { type: (event as PointerEvent).pointerType ?? '', at: performance.now() };
      },
      { capture: true },
    );
  }
}

/** A press that a software keyboard answers: a touch or a pen, or no pointer on a coarse device. */
function pressRaisesAKeyboard(): boolean {
  if (lastPointer && performance.now() - lastPointer.at < PRESS_MEMORY_MS) {
    return lastPointer.type === 'touch' || lastPointer.type === 'pen';
  }
  // An activation with no pointer press (a screen reader's double tap, Enter): the device decides.
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/**
 * The keyboard holder while it is out, the control it was held for (KD-6's opener), and the timer
 * that gives it up when no dialog opens. Releasing the holder clears that timer, so a holder
 * replaced or released early leaves no timer behind.
 */
let keyboardHolder: {
  input: HTMLInputElement;
  opener: Element | null;
  giveUp?: number;
} | null = null;

export const KEYBOARD_HOLDER_ATTRIBUTE = 'data-keyboard-holder';

function releaseKeyboardHolder(): void {
  const held = keyboardHolder;
  if (!held) return;
  keyboardHolder = null;
  if (held.giveUp !== undefined) window.clearTimeout(held.giveUp);
  held.input.remove();
}

/**
 * A field that mounts after its dialog marks where it will be with this attribute (the markdown
 * editor draws its editable surface a moment after its own element). A dialog opened by a tap with
 * one inside and no typing field yet holds the keyboard itself (KD-16a), for every opener at once.
 */
export const LATE_TYPING_FIELD_ATTRIBUTE = 'data-late-typing-field';

/**
 * Puts what was typed into the holder before the field mounted into the field, the caret at the
 * end of what it holds (KD-16a): the person typed at once, and none of it is lost. The browser's own
 * insertion where it has one, so the field's owner (React, the editor) reads it as typing.
 */
function carryTypedText(text: string, field: HTMLElement): void {
  if (!text) return;
  const plain = field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement;
  let range: Range | null = null;
  if (plain) {
    const end = field.value.length;
    field.setSelectionRange(end, end);
  } else {
    range = document.createRange();
    range.selectNodeContents(field);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
  if (
    typeof document.execCommand === 'function' &&
    document.execCommand('insertText', false, text)
  ) {
    return;
  }
  // No editing commands (jsdom): the same change, said with an input event.
  if (plain) {
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field), 'value')?.set;
    setter?.call(field, field.value + text);
  } else if (range) {
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
  field.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Moves focus from the holder to the first of `candidates` that takes it, editable to editable, so
 * the keyboard stays up, and brings along what was typed meanwhile. Returns the field, or null.
 */
function handOverFromHolder(
  holder: HTMLInputElement,
  candidates: HTMLElement[],
): HTMLElement | null {
  const typed = holder.value;
  const field = focusFirstThatTakes(candidates, { preventScroll: true });
  if (field) carryTypedText(typed, field);
  return field;
}

/** The holder, when it is the element that has focus now. */
function focusedKeyboardHolder(): HTMLInputElement | null {
  return keyboardHolder && document.activeElement === keyboardHolder.input
    ? keyboardHolder.input
    : null;
}

/**
 * KD-16a, the keyboard rises (I-1551, 0.5.8 in the iPhone app). WKWebView and iPhone Safari raise
 * the software keyboard only for a field focused inside a user gesture; a focus made later draws a
 * caret and no keyboard. A dialog whose field mounts late (the reason editor) was focused by the
 * trap hundreds of ms after the tap, so the person saw a caret and their typing went nowhere.
 *
 * Call this synchronously in the tap's own handler, before anything it awaits, when the tap opens a
 * dialog whose first field mounts late. It focuses a text input the trap owns — in the document but
 * outside the page's root, transparent and 1 px, never `display: none`, at a 16 px font so iOS does
 * not zoom, hidden from screen readers and out of the tab order. The keyboard rises for it; the trap
 * moves focus from it to the dialog's field once that mounts (editable to editable keeps the
 * keyboard up), and the holder removes itself as soon as it loses focus. A mouse click holds
 * nothing: no keyboard rises there, and focus is left to the trap as before.
 */
export function holdKeyboardForDialog(): void {
  if (typeof document === 'undefined' || !pressRaisesAKeyboard()) return;
  // Already held for this tap (its opener and the dialog's loading fallback both ask): kept as is.
  if (focusedKeyboardHolder()) return;
  releaseKeyboardHolder();
  const active = document.activeElement;
  const pressed =
    lastPressed && performance.now() - lastPressed.at < PRESS_MEMORY_MS
      ? lastPressed.element
      : null;
  // WebKit does not focus a tapped button, so the press is the opener there; a menu item's is its
  // trigger, read now while the menu is still open.
  const opener = returnTargetOf(
    active && active !== document.body && active.tagName !== 'MAIN' ? active : pressed,
  );
  const input = document.createElement('input');
  input.type = 'text';
  input.setAttribute(KEYBOARD_HOLDER_ATTRIBUTE, '');
  input.setAttribute('aria-hidden', 'true');
  input.setAttribute('autocomplete', 'off');
  input.tabIndex = -1;
  Object.assign(input.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '0',
    border: '0',
    opacity: '0',
    fontSize: '16px',
    pointerEvents: 'none',
    caretColor: 'transparent',
  });
  input.addEventListener('blur', () => {
    if (keyboardHolder?.input === input) releaseKeyboardHolder();
    else input.remove();
  });
  document.body.append(input);
  keyboardHolder = { input, opener };
  input.focus({ preventScroll: true });
  if (document.activeElement !== input) {
    releaseKeyboardHolder();
    return;
  }
  // A dialog that never opened leaves no holder behind holding the keyboard up.
  keyboardHolder.giveUp = window.setTimeout(() => {
    if (keyboardHolder?.input !== input || openTraps.length > 0) return;
    const back = keyboardHolder.opener;
    releaseKeyboardHolder();
    if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true });
  }, LATE_FIELD_MS);
}

/**
 * ⛔ `<main>` is never a real opener (captain's fix, found flaky under load, IU-8 F-0521).
 * `document.activeElement` still comes first, as it always did — a real nested-sheet case (a
 * picker opened over another sheet, `Sheet.test.tsx`) can have focus genuinely still sitting
 * somewhere inside the sheet underneath at the exact moment the new one mounts, and that is the
 * correct opener to restore to; preferring `lastPressed` unconditionally there returned focus to
 * the wrong control and broke it. The one carve-out is `<main>` itself: it only ever receives
 * focus from `ShellRuntime`'s own route-settle move (`moveFocusToScreenHeading`, IU-15,
 * `screenSettled.ts`), asynchronously, once the first screen settles — never from a person
 * pressing something to open a dialog. On a freshly loaded route that move can still be pending,
 * or can land, in the same window a reader's very next tap opens a dialog; the old code trusted
 * whichever element happened to hold focus at mount, so a dialog opened in that window returned
 * focus to `<main>` on close instead of to the control the reader actually pressed — reproduced by
 * `iphone-sheet.spec.ts`'s shell-mode focus-return case, intermittently (the two events race),
 * never in mobile web (nothing there ever moves focus to `<main>` on its own). `lastPressed` is
 * the fallback exactly there, and for an open reached with no press at all `document.activeElement`
 * (now definitely not `<main>`, usually `body`) is read again below.
 */
function openerOfDialog(): Element | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  // The keyboard holder took focus in the tap (KD-16a): the control it was held for opened this.
  if (keyboardHolder && active === keyboardHolder.input) return keyboardHolder.opener;
  const isRouteFocusTarget = active instanceof HTMLElement && active.tagName === 'MAIN';
  if (active && active !== document.body && !isRouteFocusTarget) return active;
  if (lastPressed && performance.now() - lastPressed.at < PRESS_MEMORY_MS) {
    // A menu item pressed is already gone when a sheet mounts a render later: its menu's trigger is
    // what it opened from (F-0533, Postpone at 390).
    const pressed = returnTargetOf(lastPressed.element);
    if (pressed?.isConnected) return pressed;
  }
  return document.activeElement;
}

/**
 * A menu item is never where focus should return: choosing it closes the menu, so the item is gone
 * before the dialog it opened closes (F-0533, Postpone from More actions fell to `body`). The menu's
 * trigger is — the control that names the menu in `aria-controls`.
 */
function returnTargetOf(opener: Element | null): Element | null {
  const menu = opener?.closest?.('[role="menu"]');
  if (!menu?.id) return opener;
  const trigger = Array.from(document.querySelectorAll('[aria-controls]')).find(
    (element) => element.getAttribute('aria-controls') === menu.id,
  );
  return trigger ?? opener;
}

/**
 * How to find an opener again once its node is gone (F-0533, New item): a dialog that is a route —
 * the create sheet — leaves its opener's page when it opens, and the page that comes back has the
 * same control as a new node. Its test id, else its name and text, else nothing: a control that
 * cannot be told from its neighbours is not guessed at.
 */
function findOpenerAgain(gone: HTMLElement): HTMLElement | null {
  const testId = gone.getAttribute('data-testid');
  const label = gone.getAttribute('aria-label');
  const text = gone.textContent?.trim() ?? '';
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(gone.tagName.toLowerCase()))
    .filter((element) => !element.closest('[data-modal-panel]') && !element.hidden)
    .filter((element) => {
      if (testId) return element.getAttribute('data-testid') === testId;
      if (label) return element.getAttribute('aria-label') === label;
      return text !== '' && element.textContent?.trim() === text;
    });
  return candidates.length === 1 ? candidates[0] : null;
}

const REFIND_FRAMES = 30;

/** Puts focus on the opener, or on its replacement a page drew in its place; `body` only if neither. */
function returnFocusTo(opener: HTMLElement | null): void {
  if (!opener) return;
  if (opener.isConnected) {
    opener.focus({ preventScroll: true });
    return;
  }
  const attempt = (framesLeft: number) => {
    // Someone (the dialog beneath, the person) already has focus: not ours to take.
    if (!hasNoFocus()) return;
    const again = findOpenerAgain(opener);
    if (again) again.focus({ preventScroll: true });
    else if (framesLeft > 0) requestAnimationFrame(() => attempt(framesLeft - 1));
  };
  attempt(REFIND_FRAMES);
}

/**
 * The traps open now, the topmost last (F-0533). Only the topmost takes focus back, and when one
 * closes onto an opener that is gone, the one now on top takes it instead.
 */
const openTraps: Array<{ container: HTMLElement; reclaim: () => void }> = [];

/**
 * The page a modal covers: the app's own root (`index.html`'s `#root`). Focus moved there while a
 * dialog is open went behind the dialog — the shell's route-settle move to the screen's `h1`
 * (KD-3) — and a key typed there reaches no dialog at all. An overlay rendered at the document's
 * root instead (a sheet, an anchored pop-out, a toast) is not behind the dialog, and focus that a
 * control inside it moves there is left alone; so is any other open dialog, inline or not.
 */
const PAGE_ROOT_ID = 'root';

function isBehindTheDialogs(element: Element): boolean {
  // Another dialog is a dialog, not the page: an open trap's, or a `ModalFrame` panel still mounting
  // — a control with `autoFocus` inside a new dialog takes focus before its trap's effect runs.
  if (openTraps.some((trap) => trap.container.contains(element))) return false;
  if (element.closest('[data-modal-panel]')) return false;
  return document.getElementById(PAGE_ROOT_ID)?.contains(element) ?? false;
}

/**
 * A menu or a non-modal dialog of the dialog's own (the editor toolbar's menus, a link pop-out) owns
 * an Escape typed inside it: it closes, and the dialog stays for the next Escape (KD-8). Its own
 * handler runs after this one (React's, at the app's root), so the dialog must not answer first.
 */
const INNER_OVERLAY = '[role="menu"], [role="dialog"], [role="alertdialog"]';

function ownedByAnInnerOverlay(target: EventTarget | null, container: HTMLElement | null): boolean {
  if (!container || !(target instanceof Element)) return false;
  const inner = target.closest(INNER_OVERLAY);
  return inner !== null && inner !== container && container.contains(inner);
}

function hasNoFocus(): boolean {
  return !document.activeElement || document.activeElement === document.body;
}

/**
 * I-0494. A `role="dialog"` element with no focus management is a dialog in name only: focus stays
 * on whatever opened it, `Tab` walks every control the overlay is meant to cover (including
 * destructive ones behind it), and `Escape` does nothing. This moves focus into the dialog on
 * mount, traps `Tab`/`Shift+Tab` within it, closes it on `Escape`, and returns focus to the element
 * that had it when the dialog opened — the WAI-ARIA modal dialog pattern.
 *
 * And it keeps focus inside until it closes (F-0533). Two things took it out without anyone asking:
 * a control holding focus left the dialog — a step changed, a row re-rendered — and focus fell to
 * `body` (KD-2, the Add block dialog); and something outside moved it to the page behind (KD-3,
 * the shell's route-settle move under a picker). Either way the next Escape reached no dialog.
 * The topmost dialog takes focus back — to the control inside that last held it, else to itself.
 *
 * `containerRef` must point at the dialog's own root element (the one carrying `role="dialog"`),
 * which needs `tabIndex={-1}` so it can hold focus itself when the dialog opens with nothing
 * focusable inside yet (e.g. a picker step with only non-focusable content).
 *
 * `initialFocusRef` names the control that takes focus on open instead of the first one — the safe
 * choice of a confirmation (`alertdialog`), where the first control is often the consequential one.
 */
export function useDialogFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  initialFocusRef?: RefObject<HTMLElement | null>,
): void {
  // A ref, not a dependency: the effect below moves focus exactly once, on mount, and must not
  // re-run just because the caller passed a new `onClose` closure on a later render — re-running
  // it would steal focus back from wherever the person tabbed to.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  /*
    Who opened the dialog, read on the first render. By the time an effect runs, a control inside
    with `autoFocus` has already taken focus (React focuses it during the commit), and "whatever
    had focus" would be that control — so closing would hand focus back to an element being removed.
  */
  const openerRef = useRef<Element | null>(null);
  if (openerRef.current === null) openerRef.current = returnTargetOf(openerOfDialog());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const previouslyFocused = openerRef.current as HTMLElement | null;

    // Registered before focus moves in, so the dialog underneath sees this one as a dialog.
    let lastInside: HTMLElement | null = container.contains(document.activeElement)
      ? (document.activeElement as HTMLElement)
      : null;
    const reclaim = () => {
      const target =
        lastInside && lastInside.isConnected && container.contains(lastInside)
          ? lastInside
          : container;
      target.focus({ preventScroll: true });
    };
    const trap = { container, reclaim };
    openTraps.push(trap);
    // Its place among every open overlay, so a pop-out opened before it leaves Escape to it (KD-8).
    const leaveOrder = registerDialogOrder(nextOverlayOrder());
    const isTopmost = () => openTraps[openTraps.length - 1] === trap;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Element | null;
      if (target instanceof HTMLElement && container.contains(target)) {
        lastInside = target;
        return;
      }
      if (isTopmost() && target && isBehindTheDialogs(target)) reclaim();
    };
    document.addEventListener('focusin', onFocusIn, true);

    // A control that already took focus inside (`autoFocus`) keeps it; otherwise the named one, else
    // `initialFocusTarget`'s. Measured: a trap that always moved focus to the first control took it
    // off the attach dialog's search field, and ArrowDown no longer moved through the results.
    let placed: Element | null = null;
    const placeFocus = () => {
      focusFirstThatTakes(initialFocusCandidates(container));
      placed = document.activeElement;
    };
    /*
      KD-16a, the keyboard rises: while the holder the tap focused still has focus, nothing that
      takes no typing is focused (that would drop the keyboard). A field already there takes focus
      from it now; otherwise the holder keeps it until the field mounts, as the trap's own placement.
    */
    const fieldIsComing = () =>
      typingFields(container).length === 0 &&
      container.querySelector(`[${LATE_TYPING_FIELD_ATTRIBUTE}]`) !== null;
    let holder = focusedKeyboardHolder();
    // Opened by a tap with its field still to come: the dialog holds the keyboard itself, in the
    // tap if it mounted in it — every opener of such a dialog, without one of them having to ask.
    if (
      !holder &&
      !initialFocusRef?.current &&
      !container.contains(document.activeElement) &&
      fieldIsComing()
    ) {
      holdKeyboardForDialog();
      holder = focusedKeyboardHolder();
    }
    if (initialFocusRef?.current) {
      if (holder) handOverFromHolder(holder, [initialFocusRef.current]);
      else initialFocusRef.current.focus();
    } else if (holder) {
      if (!handOverFromHolder(holder, typingFields(container))) {
        // A field still to come keeps the keyboard up for it; with none, the dialog is placed now.
        if (fieldIsComing()) placed = holder;
        else placeFocus();
      }
    } else if (!container.contains(document.activeElement)) {
      placeFocus();
    }
    /*
      KD-16a (I-1551): a dialog whose field mounts after the dialog (the reason editor of Resolve,
      Close and Cancel) opened on whatever the trap found then — its first button, else the panel —
      and the first key typed went nowhere. While focus is still exactly where the trap put it, the
      first typing field to appear takes it, once. A person who pressed or typed first keeps theirs,
      and so does a dialog that named its own surface (`initialFocusRef`).
    */
    let awaitingField =
      placed !== null && (placed === holder || !(placed as Element).matches(TAKES_TYPING));
    const stopAwaiting = () => {
      awaitingField = false;
      // The wait is over with the holder still up: the dialog is placed by KD-16's rule instead.
      if (holder && document.activeElement === holder && isTopmost()) placeFocus();
    };
    const lateFieldDeadline = window.setTimeout(stopAwaiting, LATE_FIELD_MS);
    const takeLateField = () => {
      if (!awaitingField || document.activeElement !== placed) return;
      const fields = typingFields(container);
      const took =
        holder && placed === holder
          ? handOverFromHolder(holder, fields)
          : focusFirstThatTakes(fields, { preventScroll: true });
      if (!took) return;
      awaitingField = false;
    };
    container.addEventListener('pointerdown', stopAwaiting, true);
    container.addEventListener('keydown', stopAwaiting, true);

    // A focused control removed or disabled moves focus to `body` without a focus event in Chromium,
    // so the dialog's own changes are watched instead: after each, focus that fell out comes back.
    const lostFocusWatch = new MutationObserver(() => {
      /*
        KD-16a at a phone's width: the trap placed focus on the editor toolbar's first stop (Undo),
        the toolbar then fitted itself to 390 and moved Undo into its overflow, and focus fell out.
        Taken back to the panel, it was no longer where the trap had put it, so the editor that
        mounted next never took it. While the trap still waits, its own placement is placed again.
      */
      if (awaitingField && isTopmost() && hasNoFocus()) placeFocus();
      takeLateField();
      if (isTopmost() && hasNoFocus()) reclaim();
    });
    lostFocusWatch.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'inert', 'tabindex', 'contenteditable'],
    });

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        // The field's own handler cancels the edit; the dialog stays for the next Escape.
        if (event.target instanceof Element && event.target.closest(`[${INLINE_EDIT_ATTRIBUTE}]`)) {
          return;
        }
        // A pop-out inside already closed on this key, or an inner menu will (KD-8).
        if (escapeSpent(event) || ownedByAnInnerOverlay(event.target, container)) return;
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      // A control inside that already took Tab for itself (an editor's indent) keeps it.
      if (event.key !== 'Tab' || !container || event.defaultPrevented) return;
      /*
        Every Tab moves focus here, never the browser. WebKit's Tab skips buttons unless the person
        turned on "Press Tab to highlight each item" (Safari, and iOS with a hardware keyboard), so
        the last *button* is never where its Tab starts, the wrap below never ran, and Tab from the
        last field left the dialog for the page behind it (IU-3b, measured in WebKit on the sheet).
      */
      event.preventDefault();
      const elements = focusableElements(container);
      if (elements.length === 0) return;
      const at = elements.indexOf(document.activeElement as HTMLElement);
      const step = event.shiftKey ? -1 : 1;
      const first = at === -1 ? (event.shiftKey ? elements.length - 1 : 0) : at + step;
      // Every stop after this one in the key's direction, round the end, and past one that does not
      // take focus (KD-16a, Tab goes round): the editor's undrawn link tooltip held Tab on the editor.
      const onward = elements
        .map(
          (_, offset) =>
            elements[
              (((first + step * offset) % elements.length) + elements.length) % elements.length
            ],
        )
        .filter((element) => element !== document.activeElement);
      focusFirstThatTakes(onward);
    }

    container.addEventListener('keydown', onKeyDown);
    // A key on the holder (a hardware keyboard's Escape or Tab) is the dialog's, as inside it.
    holder?.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      holder?.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn, true);
      lostFocusWatch.disconnect();
      window.clearTimeout(lateFieldDeadline);
      container.removeEventListener('pointerdown', stopAwaiting, true);
      container.removeEventListener('keydown', stopAwaiting, true);
      leaveOrder();
      const at = openTraps.indexOf(trap);
      if (at !== -1) openTraps.splice(at, 1);
      // I-1700: a dialog still in the document has not closed. `StrictMode` (the dev server) runs
      // this cleanup and the effect again on mount, and handing focus to the opener here bounced it
      // field, opener, field: the blur marked Postpone's choice as left before anyone touched it.
      // React removes a closing dialog's elements before its cleanups run, so a real close is never
      // this.
      if (container.isConnected) return;
      // Unmounting a focused element blurs it to `body` before this cleanup runs (in both jsdom and
      // real browsers), so checking "does the dialog still hold focus" here would always read false.
      // The WAI-ARIA modal pattern returns focus to the trigger unconditionally on close, which is
      // also the simpler, always-correct rule.
      //
      // Without scrolling (I-1297, AC-19.11): the reader has not moved under the dialog, so the
      // page stays where it is. In the app the opener of a dialog asked for by the shell is the
      // screen's own `h1` at the page's top (`ShellRuntime`'s route-settle focus), and a plain
      // `focus()` scrolled a list at 500 back to 0 on Cancel.
      // An opener that is gone (re-rendered with its new value, KD-3) takes no focus: the dialog
      // underneath, if one is still open, takes it instead of `body`; with none, its replacement.
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
      if (holder && keyboardHolder?.input === holder) releaseKeyboardHolder();
      if (hasNoFocus()) {
        const below = openTraps[openTraps.length - 1];
        if (below) below.reclaim();
        else returnFocusTo(previouslyFocused);
      }
    };
    // Intentionally `[containerRef]` only: this must run once per mount of the dialog. `onClose` is
    // read through `onCloseRef` above precisely so it never needs to be a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);
}
